export type SfyrContextSubscribeCallback = () => void;

export class SfyrContext<
  Context extends Record<string, any> = Record<string, any>,
> {
  private _context: Context;
  private readonly listeners: Set<SfyrContextSubscribeCallback> = new Set();
  private version = 0;

  constructor(initial: Context) {
    this._context = { ...initial };
  }

  /**
   * Get a context value by key.
   */
  get<K extends keyof Context>(key: K): Context[K] {
    return this._context[key];
  }

  /**
   * Get all context values as a record.
   */
  getAll(): Context {
    return { ...this._context };
  }

  /**
   * Get current version (increments on each change).
   */
  getVersion(): number {
    return this.version;
  }

  /**
   * Subscribe to context changes.
   * Returns an unsubscribe function.
   */
  subscribe(callback: SfyrContextSubscribeCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Update context values.
   */
  readonly update = (cb: (prev: Context) => Partial<Context>) => {
    const next = cb(this._context);
    let changed = false;
    for (const [key, value] of Object.entries(next)) {
      if (value !== undefined && this._context[key as keyof Context] !== value) {
        this._context[key as keyof Context] = value;
        changed = true;
      }
    }
    if (changed) {
      this.version++;
      this.notifyListeners();
    }
  };
}
